import { FilterOption } from './types';

/**
 * Counts the total number of selected filter options across all groups
 */
export function countTotalSelected(value: Record<string, string[]>): number {
  return Object.values(value).reduce((total, groupValues) => {
    return total + groupValues.length;
  }, 0);
}

/**
 * Counts the number of selected options within a specific filter group
 */
export function countGroupSelected(
  groupId: string,
  value: Record<string, string[]>
): number {
  return value[groupId]?.length || 0;
}

/**
 * Filters options based on search text
 */
export function filterOptions(
  options: FilterOption[],
  searchText: string
): FilterOption[] {
  if (!searchText) return options;

  const normalizedSearchText = searchText.toLowerCase();

  return options.filter((option) => {
    const label =
      typeof option.label === 'string' ? option.label.toLowerCase() : '';
    return label.includes(normalizedSearchText);
  });
}

/**
 * Creates a new value object with all selections cleared
 */
export function clearAllSelections(
  value: Record<string, string[]>
): Record<string, string[]> {
  return Object.keys(value).reduce(
    (acc, groupId) => {
      acc[groupId] = [];
      return acc;
    },
    {} as Record<string, string[]>
  );
}

/**
 * Toggles a specific option in a group
 */
export function toggleOption(
  groupId: string,
  optionValue: string,
  isMulti: boolean,
  currentValue: Record<string, string[]>
): Record<string, string[]> {
  const currentGroupValues = currentValue[groupId] || [];

  // For single select groups, replace the entire array
  if (!isMulti) {
    // If the option is already selected, do nothing
    if (currentGroupValues.includes(optionValue)) {
      return currentValue;
    }

    return {
      ...currentValue,
      [groupId]: [optionValue],
    };
  }

  // For multi-select, toggle the option
  const newGroupValues = currentGroupValues.includes(optionValue)
    ? currentGroupValues.filter((v) => v !== optionValue)
    : [...currentGroupValues, optionValue];

  return {
    ...currentValue,
    [groupId]: newGroupValues,
  };
}
